/* Theme custom file */
/**
 * Predictive search (alternate) — Algolia-powered.
 *
 * A custom copy of Horizon's predictive-search.js that protects the native file
 * (which stays pristine and auto-updating). It keeps Horizon's shell behaviour
 * (debounce, keyboard navigation, morph) and swaps the result fetch to Algolia,
 * rendering products client-side into Horizon/Bradery card markup.
 *
 * State model (see docs/specs/search-algolia.md):
 *  - Empty (no query): the server-rendered operations showcase + trending pills.
 *  - Query (typing):   Algolia products; operations and trending pills hidden.
 *
 * Credentials, helpers and the product card come from the shared adapter module
 * (assets/algolia-search.js), reading the public, search-only Algolia config block
 * (`#tb-algolia-config`) emitted by header-alternate.liquid — no key in this file.
 * Any Algolia failure falls back to native predictive search (section render).
 */

import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { CONFIG, algoliaReady, algoliaRequest, escapeHtml, renderProductCard, recordSearchClick, writeAlgoliaConfig } from '@theme/algolia-search';

/* -------------------------------------------------------------------------- *
 * Algolia adapter
 * -------------------------------------------------------------------------- */

/**
 * One Algolia index query.
 * @param {string} index
 * @param {string} term
 * @param {number} hitsPerPage
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ hits?: any[] }>}
 */
function algoliaQuery(index, term, hitsPerPage, signal) {
  return algoliaRequest(
    `/1/indexes/${encodeURIComponent(index)}/query`,
    { query: term, hitsPerPage, clickAnalytics: true },
    signal,
  );
}

/**
 * Products plus query suggestions, in parallel.
 * @param {string} term
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ products: any[], suggestions: string[], queryID?: string, index: string }>}
 */
async function algoliaSearch(term, signal) {
  const { productsIndex, suggestionsIndex } = /** @type {NonNullable<typeof CONFIG>} */ (CONFIG);
  const [products, suggestions] = await Promise.all([
    algoliaQuery(productsIndex, term, 8, signal),
    suggestionsIndex ? algoliaQuery(suggestionsIndex, term, 6, signal) : Promise.resolve({ hits: [] }),
  ]);
  return {
    products: products.hits ?? [],
    suggestions: (suggestions.hits ?? []).map((hit) => hit.query).filter(Boolean),
    queryID: products.queryID,
    index: productsIndex,
  };
}

/**
 * One product card list item, wrapping the shared card in the dropdown's markup.
 * The Insights data attributes let the click handler attribute the search to a conversion.
 * @param {any} hit
 * @param {string} [queryID]
 * @param {number} [position] - 1-based position in the result list
 * @param {string} [index]
 */
function renderProductCardItem(hit, queryID, position, index) {
  const insights =
    queryID && hit.objectID != null
      ? ` data-object-id="${escapeHtml(String(hit.objectID))}" data-product-id="${escapeHtml(String(hit.id ?? ''))}" data-query-id="${escapeHtml(queryID)}" data-position="${position}" data-index="${escapeHtml(index ?? '')}"`
      : '';
  return `<li class="predictive-search-results__card predictive-search-results__card--product" ref="resultsItems[]"${insights}>${renderProductCard(
    hit,
    { soldOutLabel: CONFIG?.labels?.soldOut ?? '' }
  )}</li>`;
}

/**
 * One Algolia query-suggestion pill. Keyboard-navigable (matches the refs the
 * shell's navigation looks for) and styled like the default trending pills.
 * @param {string} query
 */
function renderSuggestionPill(query) {
  return `<li class="predictive-search-results__card--query mini-search__suggestion" ref="resultsItems[]">
    <a class="mini-search__trending-pill" href="${Theme.routes.search_url}?q=${encodeURIComponent(query)}">${escapeHtml(query)}</a>
  </li>`;
}

/**
 * Build the query-state dropdown markup from Algolia data (suggestions + products).
 * @param {{ products: any[], suggestions: string[] }} data
 * @param {string} term
 */
function renderResults({ products, suggestions, queryID, index }, term) {
  const suggestionsBlock = suggestions.length
    ? `<ul class="predictive-search-results__wrapper-queries mini-search__trending-pills list-unstyled" role="listbox">
         ${suggestions.map(renderSuggestionPill).join('')}
       </ul>`
    : '';

  const productsBlock = products.length
    ? `<ul class="predictive-search-results__list predictive-search-results__wrapper-products list-unstyled" role="listbox">
         ${products.map((hit, i) => renderProductCardItem(hit, queryID, i + 1, index)).join('')}
       </ul>`
    : `<p class="predictive-search-results__no-results">${(CONFIG?.labels?.noResultsTemplate ?? '').replace(
        '__TERM__',
        escapeHtml(term)
      )}</p>`;

  return `<div id="predictive-search-results" class="predictive-search-dropdown" role="listbox" aria-label="${escapeHtml(
    CONFIG?.labels?.resultsLabel ?? ''
  )}" aria-expanded="true">
    <div class="predictive-search-results__inner">${suggestionsBlock}${productsBlock}</div>
  </div>`;
}

/* -------------------------------------------------------------------------- *
 * Component
 * -------------------------------------------------------------------------- */

/**
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput
 * @property {HTMLElement} predictiveSearchResults
 * @property {HTMLElement} resetButton
 * @property {HTMLElement[]} [resultsItems]
 * @extends {Component<Refs>}
 */
class PredictiveSearchAlternate extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** Server-rendered empty state (operations showcase), captured on connect. */
  #emptyMarkup = '';

  connectedCallback() {
    super.connectedCallback();
    this.#emptyMarkup = this.refs.predictiveSearchResults.innerHTML;
    writeAlgoliaConfig();
    this.refs.predictiveSearchResults.addEventListener('click', this.#onSearchResultClick, {
      signal: this.#controller.signal,
    });
  }

  /**
   * Fire the Algolia click event and store the click so the Customer Events pixel can
   * attribute a later add-to-cart or purchase to this search. Passive, never blocks navigation.
   * @param {Event} event
   */
  #onSearchResultClick = (event) => {
    const card =
      event.target instanceof Element ? event.target.closest('[data-object-id][data-query-id]') : null;
    if (!card) return;
    recordSearchClick(
      { objectID: card.getAttribute('data-object-id'), id: card.getAttribute('data-product-id') || undefined },
      card.getAttribute('data-query-id'),
      Number(card.getAttribute('data-position')) || 1,
      card.getAttribute('data-index') || CONFIG.productsIndex,
    );
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /** @type {boolean} */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => item.classList.remove('keyboard-focus'));

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) item.classList.add('keyboard-focus');
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate results with arrow keys, or close on Escape.
   * @param {KeyboardEvent} event
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        if (event.shiftKey) {
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Debounced input handler. Empty term restores the empty state; otherwise
   * fetch and render Algolia results.
   */
  search = debounce((event) => {
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#getSearchResults(searchTerm);
  }, 200);

  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content, .mini-search__results')?.scrollTo(0, 0);
    });
  }

  /**
   * Query state — render Algolia products. Falls back to native predictive
   * search (section render) on any Algolia failure or missing config.
   * @param {string} searchTerm
   */
  async #getSearchResults(searchTerm) {
    const { predictiveSearchResults } = this.refs;
    const abortController = this.#createAbortController();

    if (algoliaReady()) {
      try {
        const data = await algoliaSearch(searchTerm, abortController.signal);
        if (abortController.signal.aborted) return;
        morph(predictiveSearchResults, renderResults(data, searchTerm));
        this.#resetScrollPositions();
        return;
      } catch (error) {
        if (abortController.signal.aborted) return;
        console.error('[algolia] predictive search failed, falling back to native', error);
      }
    }

    // Native Shopify predictive search (fallback).
    if (!this.dataset.sectionId) return;
    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup || abortController.signal.aborted) return;
        morph(predictiveSearchResults, resultsMarkup);
        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) this.#activeFetch.abort();
    this.#activeFetch = abortController;
    return abortController;
  }

  /** Empty state — restore the server-rendered operations showcase + trending. */
  #resetSearch = () => {
    const { predictiveSearchResults, searchInput } = this.refs;

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#createAbortController();

    // Restore the captured empty state. morph reconciles children, so we pass the
    // captured innerHTML (root `#predictive-search-results`) the same way the query
    // path morphs the ref with a section whose root is `#predictive-search-results`.
    morph(predictiveSearchResults, this.#emptyMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-alternate')) {
  customElements.define('predictive-search-alternate', PredictiveSearchAlternate);
}
